import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: { paddingVertical: 20, paddingHorizontal: 20, position: "relative" },
  header: { marginBottom: 0, textAlign: "center" },
  reportTitle: { fontSize: 12, fontWeight: "bold", marginTop: 2, marginBottom: 4 },
  shopInfo: { fontSize: 10 },
  table: { display: "table", width: "100%", marginTop: 2, borderWidth: 1, borderColor: "#000" },
  tableRow: { flexDirection: "row", borderBottomWidth: 0.5, borderColor: "#000" },
  tableColHeader: {
    paddingVertical: 5,
    paddingHorizontal: 3,
    color: "#000232",
    textAlign: "center",
    fontSize: 10,
    fontWeight: "bold",
    backgroundColor: "#E7EBF4",
  },
  tableCol: { padding: 5, textAlign: "center", fontSize: 10, borderWidth: 0.5, borderColor: "#000" },
  tableColGrandTotal: { padding: 5, textAlign: "center", fontSize: 10 },
  tableColSl: { flex: 0.3 },
  tableColDate: { flex: 1 },
  tableColInvoice: { flex: 0.8 },
  tableColCustomer: { flex: 2 },
  tableColProduct: { flex: 3 }, // ✅ adjusted to take space after removing Order Type & IMEI
  tableColSalesAmount: { flex: 1.2 }, // ✅ slightly wider for readability
  tableRowAlternate: { backgroundColor: "#F5F5F5" },
  grandTotalRow: { backgroundColor: "#E7EBF4", fontWeight: "bold" },
  footer: {
    position: "absolute",
    bottom: 15,
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "space-around",
    fontSize: 10,
  },
});

const ROWS_PER_PAGE = 20; // ✅ adjust based on layout

const TableHeader = () => (
  <View style={styles.tableRow}>
    <Text style={[styles.tableColHeader, styles.tableColSl]}>Sl.</Text>
    <Text style={[styles.tableColHeader, styles.tableColDate]}>Date</Text>
    <Text style={[styles.tableColHeader, styles.tableColInvoice]}>Invoice</Text>
    <Text style={[styles.tableColHeader, styles.tableColCustomer]}>Customer Name</Text>
    <Text style={[styles.tableColHeader, styles.tableColProduct]}>Product Name</Text>
    <Text style={[styles.tableColHeader, styles.tableColSalesAmount]}>Amount (BDT)</Text>
    <Text style={[styles.tableColHeader, styles.tableColSalesAmount]}>Purchase Amount (BDT)</Text>
    <Text style={[styles.tableColHeader, styles.tableColSalesAmount]}>Profit (BDT)</Text>
  </View>
);

const MonthlySalesDayCountPortraitPDFDocument = ({
  reports,
  startDate,
  endDate,
  dayCount,
  grandTotal,
  profitTotal,
  discountTotal,
  selected
}) => {
  const totalPages = Math.ceil(reports.length / ROWS_PER_PAGE);

  return (
    <Document>
      {Array.from({ length: totalPages }).map((_, pageIndex) => {
        const pageData = reports.slice(
          pageIndex * ROWS_PER_PAGE,
          (pageIndex + 1) * ROWS_PER_PAGE
        );

        return (
          <Page key={pageIndex} style={styles.page} size="A4" orientation="portrait">
            {/* Header */}
            <View style={styles.header}>
              <Text style={styles.reportTitle}>Monthly Sales Day Counting Report</Text>
              <Text style={styles.shopInfo}>({startDate} to {endDate})</Text>
              <Text>Page {pageIndex + 1} of {totalPages}</Text>
            </View>

            {/* Table */}
            <View style={styles.table}>
              <TableHeader />

              {pageData.map((report, index) => (
                <View
                  key={index}
                  style={[styles.tableRow, index % 2 === 0 && styles.tableRowAlternate]}
                >
                  <Text style={[styles.tableCol, styles.tableColSl]}>
                    {pageIndex * ROWS_PER_PAGE + index + 1}
                  </Text>
                  <Text style={[styles.tableCol, styles.tableColDate]}>
                    {report?.date || "N/A"}
                  </Text>
                  <Text style={[styles.tableCol, styles.tableColInvoice]}>
                    {report?.invoice_id?.split("-").pop() || "N/A"}
                  </Text>
                  <Text style={[styles.tableCol, styles.tableColCustomer]}>
                    {report?.customer_name || "N/A"}
                  </Text>
                  <Text style={[styles.tableCol, styles.tableColProduct]}>
                    {report?.product_name || "N/A"}
                  </Text>
                  <Text style={[styles.tableCol, styles.tableColSalesAmount]}>
                    {Number(report?.price || 0).toFixed(2)}
                  </Text>
                  <Text style={[styles.tableCol, styles.tableColSalesAmount]}>
                    {Number(report?.purchase_price || 0).toFixed(2)}
                  </Text>
                  <Text style={[styles.tableCol, styles.tableColSalesAmount]}>
                    {Number(
                      ((report?.price || 0) - (report?.purchase_price || 0)) *
                        (report?.qty || 0)
                    ).toFixed(2)}
                  </Text>
                </View>
              ))}

              {/* Show Grand Total only on Last Page */}
              {pageIndex === totalPages - 1 && (
                <>
                  <View style={[styles.tableRow, styles.grandTotalRow]}>
                    <Text style={[styles.tableColGrandTotal, styles.tableColSl]}></Text>
                    <Text style={[styles.tableCol, styles.tableColDate]}>
                      {dayCount || 0} Day(s)
                    </Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColInvoice]}></Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColCustomer]}></Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColProduct]}>
                      Grand Total
                    </Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColSalesAmount]}>
                      {grandTotal || 0}
                    </Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColSalesAmount]}>
                      {profitTotal || 0}
                    </Text>
                  </View>
                  {selected === 'All' && <View style={[styles.tableRow, styles.grandTotalRow]}>
                    <Text style={[styles.tableColGrandTotal, styles.tableColSl]}></Text>
                    <Text style={[styles.tableCol, styles.tableColDate]}>
                    </Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColInvoice]}></Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColCustomer]}></Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColProduct]}>
                      Discount Total
                    </Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColSalesAmount]}>
                      {discountTotal || 0}
                    </Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColSalesAmount]}>
                    </Text>
                  </View>}
                  <View style={[styles.tableRow, styles.grandTotalRow]}>
                    <Text style={[styles.tableColGrandTotal, styles.tableColSl]}></Text>
                    <Text style={[styles.tableCol, styles.tableColDate]}>
                    </Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColInvoice]}></Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColCustomer]}></Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColProduct]}>
                      Actual Grand Total
                    </Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColSalesAmount]}>
                      {grandTotal - discountTotal || 0}
                    </Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColSalesAmount]}>
                    </Text>
                  </View>
                  <View style={[styles.tableRow, styles.grandTotalRow]}>
                    <Text style={[styles.tableColGrandTotal, styles.tableColSl]}></Text>
                    <Text style={[styles.tableCol, styles.tableColDate]}>
                    </Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColInvoice]}></Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColCustomer]}></Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColProduct]}>
                      Profit Total
                    </Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColSalesAmount]}>
                      {profitTotal - discountTotal || 0}
                    </Text>
                    <Text style={[styles.tableColGrandTotal, styles.tableColSalesAmount]}>
                    </Text>
                  </View>
                </>

              )}
            </View>

            {/* Footer */}
            <View style={styles.footer}>
              <Text>Prepared by</Text>
              <Text>Checked/Recommended by</Text>
              <Text>Authorized by</Text>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

export default MonthlySalesDayCountPortraitPDFDocument;
